import { forwardRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Column } from "@components/EditableDataTable/types/column";
import { useV2ListExpenseCategoriessForAccountQuery } from "@generated";
import BlueprintItemsTab, {
  TabRow,
  BlueprintItemsTabHandle,
} from "./BlueprintItemsTab";
import type { BlueprintDetail } from "../../common/api";

const tabColumns: Column[] = [
  {
    header: () => "Name/Description",
    cell: (row) => row.name || row.description,
  },
  { id: "quantity", header: () => "Quantity", cell: (row) => row.quantity },
  { header: () => "Rate", cell: (row) => row.rate },
];

const catalogColumns: ColumnDef<TabRow>[] = [
  { header: "Name", cell: (props) => props.row.original.name },
  { header: "Rate", accessorKey: "rate" },
];

const toPayloadItem = (row: TabRow) => ({
  lid: crypto.randomUUID(),
  type: "project-expenses",
  attributes: {
    description: row.name,
    rate: row.rate || 0,
    quantity: 1,
  },
  relationships: {
    "expense-category": {
      data: { id: Number(row.id), type: "expense-categories" },
    },
  },
});

const ExpenseTab = forwardRef<
  BlueprintItemsTabHandle,
  { blueprint: BlueprintDetail; refetch: () => void; readOnly?: boolean }
>(({ blueprint, refetch, readOnly }, ref) => {
  const [search, setSearch] = useState("");

  const { data, isLoading } = useV2ListExpenseCategoriessForAccountQuery({
    filter: { active: true } as any,
    page: { number: 1, size: 500 },
  });

  const toTabRow = (item: any, idx: number): TabRow => {
    const expenseCategory = data?.data?.find(
      (e) => e.id == item.relationships?.["expense-category"]?.data?.id
    );
    return {
      id: String(item.id ?? item.lid ?? `e_${idx}`),
      name: expenseCategory?.attributes?.name || "",
      description: item.attributes?.description || "",
      quantity: item.attributes?.quantity ?? 1,
      rate: item.attributes?.rate ?? "",
    };
  };

  const allItems: TabRow[] = (data?.data ?? []).map((e) => ({
    id: String(e.id),
    name: e.attributes?.name || "",
    rate: e.attributes?.rate ?? "",
  }));

  const catalogItems = search
    ? allItems.filter((item) =>
        item.name.toLowerCase().includes(search.toLowerCase())
      )
    : allItems;

  return (
    <BlueprintItemsTab
      ref={ref}
      blueprint={blueprint}
      refetch={refetch}
      payloadType="project-expenses"
      toTabRow={toTabRow}
      toPayloadItem={toPayloadItem}
      tabColumns={tabColumns}
      catalogColumns={catalogColumns}
      catalogItems={catalogItems}
      catalogLoading={isLoading}
      onCatalogSearch={(q) => setSearch(q)}
      itemLabel="Expenses"
      editableQuantity
      readOnly={readOnly}
    />
  );
});

ExpenseTab.displayName = "ExpenseTab";
export default ExpenseTab;
